import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Card, ConfigProvider, Form, Input, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BrandMark } from '../components/BrandMark';
import { buildTheme } from '../theme/antdTheme';
import { brand, brandGradient } from '../theme/tokens';

/** Sign-in is always Deep Teal, like the app's own auth screens, whatever theme the dashboard uses. */
const loginTheme = buildTheme('dark');

export function LoginPage() {
  const { user, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    document.title = 'Sign in · Cute Live Admin';
  }, []);
  if (user) return <Navigate to="/" replace />;

  return (
    <ConfigProvider theme={loginTheme}>
      <main
        style={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
          background: `radial-gradient(120% 80% at 100% 0%, ${brand.primaryContainer}66 0%, transparent 60%), ${brand.backgroundDeep}`,
          colorScheme: 'dark',
        }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, color: brand.textPrimary }}>
            <BrandMark size={44} />
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>Cute Live</div>
              <div style={{ fontSize: 13, color: brand.textSecondary }}>Admin console</div>
            </div>
          </div>
          <Card
            styles={{ body: { padding: 'clamp(20px, 5vw, 32px)' } }}
            style={{ border: `1px solid ${brand.outlineVariant}`, boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)', overflow: 'hidden' }}
          >
            <div style={{ height: 4, margin: 'calc(-1 * clamp(20px, 5vw, 32px)) calc(-1 * clamp(20px, 5vw, 32px)) 24px', background: brandGradient }} />
            <Typography.Title level={1} style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>
              Sign in
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
              Use your staff account to continue.
            </Typography.Paragraph>
            {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} role="alert" />}
            <Form
              layout="vertical"
              size="large"
              requiredMark={false}
              disabled={busy}
              onFinish={async (v: { email: string; password: string }) => {
                setBusy(true);
                setError(null);
                try {
                  await login(v.email.trim(), v.password);
                } catch (e) {
                  setError(errorMessage(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
                <Input prefix={<MailOutlined />} type="email" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus />
              </Form.Item>
              <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Enter your password' }]}>
                <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block loading={busy} style={{ marginTop: 8, height: 48, borderRadius: 24, fontWeight: 600 }}>
                Sign in
              </Button>
            </Form>
          </Card>
        </div>
      </main>
    </ConfigProvider>
  );
}
